/**
 * MotorFinancieroTab — Admin pane for the hybrid pricing model (v1.7).
 *
 * Exposes the three levers that drive Nexus revenue:
 *   1. Global multiplier (default 10x)
 *   2. Per-intent multipliers (chat, create, repair, deploy, …)
 *   3. Per-intent floors — the "hybrid" model:
 *      credits_charged = max(tokens × multiplier, floor_by_intent)
 *   4. Voice-mode config (provider + price per minute)
 *
 * All values are editable and persist via PUT /api/admin/pricing/config.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Sliders, Save, RotateCcw, Coins, MessageCircle, Wrench, Sparkles, Rocket, Search, Cpu, Mic, Volume2, Info } from 'lucide-react';
import { apiService } from '../../services/api';
import { toast } from 'sonner';

type IntentKey = 'chat' | 'analyze' | 'modify' | 'repair' | 'create' | 'optimize' | 'deploy';

const INTENT_META: Record<IntentKey, { label: string; icon: any; hint: string }> = {
  chat:     { label: 'Chat',            icon: MessageCircle, hint: 'Conversaciones y preguntas del asistente.' },
  analyze:  { label: 'Análisis',        icon: Search,        hint: 'Auditorías de código y arquitectura.' },
  modify:   { label: 'Modificar',       icon: Sliders,       hint: 'Cambios sobre un proyecto existente.' },
  repair:   { label: 'Reparar',         icon: Wrench,        hint: 'Debugging y fixes de bugs.' },
  create:   { label: 'Crear',           icon: Sparkles,      hint: 'Generación de apps y landing pages.' },
  optimize: { label: 'Optimizar',       icon: Cpu,           hint: 'Refactors y mejoras de performance.' },
  deploy:   { label: 'Deploy',          icon: Rocket,        hint: 'Publicación y despliegue.' },
};

const INTENT_ORDER: IntentKey[] = ['chat', 'analyze', 'modify', 'repair', 'create', 'optimize', 'deploy'];

interface PricingConfigDoc {
  global_multiplier: number;
  credit_value_usd: number;
  welcome_gift_credits: number;
  min_charge_credits: number;
  intent_multipliers: Record<string, string | number>;
  min_charge_by_intent: Record<string, Record<string, number>>;
  voice_config: {
    provider: string;
    price_per_minute_credits: number;
    premium_provider: string;
    premium_price_credits: number;
    free_minutes_per_day: number;
  };
}

export const MotorFinancieroTab: React.FC = () => {
  const [cfg, setCfg] = useState<PricingConfigDoc | null>(null);
  const [initial, setInitial] = useState<PricingConfigDoc | null>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiService.getAdminPricing();
      const parsed: PricingConfigDoc = {
        global_multiplier: Number(r.config.global_multiplier),
        credit_value_usd: Number(r.config.credit_value_usd),
        welcome_gift_credits: Number(r.config.welcome_gift_credits),
        min_charge_credits: Number(r.config.min_charge_credits),
        intent_multipliers: r.config.intent_multipliers || {},
        min_charge_by_intent: r.config.min_charge_by_intent || {},
        voice_config: r.config.voice_config || {
          provider: 'web_speech', price_per_minute_credits: 0,
          premium_provider: 'openai_tts', premium_price_credits: 0.5, free_minutes_per_day: 999,
        },
      };
      setCfg(parsed);
      setInitial(JSON.parse(JSON.stringify(parsed)));
    } catch (err: any) {
      toast.error('No se pudo cargar la configuración financiera');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const dirty = useMemo(() => JSON.stringify(cfg) !== JSON.stringify(initial), [cfg, initial]);

  const save = async () => {
    if (!cfg || !dirty) return;
    setSaving(true);
    try {
      await apiService.updateAdminPricingConfig({
        global_multiplier: cfg.global_multiplier,
        credit_value_usd: cfg.credit_value_usd,
        welcome_gift_credits: cfg.welcome_gift_credits,
        min_charge_credits: cfg.min_charge_credits,
        intent_multipliers: cfg.intent_multipliers,
        min_charge_by_intent: cfg.min_charge_by_intent,
        voice_config: cfg.voice_config,
      });
      toast.success('Motor financiero actualizado.');
      setInitial(JSON.parse(JSON.stringify(cfg)));
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !cfg) {
    return <div className="text-center py-16 text-slate-500">Cargando configuración…</div>;
  }

  const setIntentMultiplier = (key: IntentKey, val: number) => {
    setCfg({ ...cfg, intent_multipliers: { ...cfg.intent_multipliers, [key]: String(val) } });
  };
  const setFloor = (key: IntentKey, scope: string, val: number) => {
    const cur = cfg.min_charge_by_intent[key] || {};
    setCfg({
      ...cfg,
      min_charge_by_intent: {
        ...cfg.min_charge_by_intent,
        [key]: { ...cur, [scope]: val },
      },
    });
  };
  const setVoice = (patch: Partial<PricingConfigDoc['voice_config']>) => {
    setCfg({ ...cfg, voice_config: { ...cfg.voice_config, ...patch } });
  };

  // Simulation — worst case app: 500K tokens at avg $2/1M input, $10/1M output
  const simRealCost = (tok: number) => (tok / 1_000_000) * 6; // rough weighted avg
  const simCharged = (tok: number, mult: number) => simRealCost(tok) * mult;
  const simCredits = (usd: number) => usd / cfg.credit_value_usd;

  return (
    <div className="space-y-6" data-testid="motor-financiero-tab">
      {/* Header row */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold" style={{ color: 'hsl(var(--text))' }}>Motor financiero</h2>
          <p className="text-xs mt-1" style={{ color: 'hsl(var(--text-muted))' }}>
            Controla multiplicadores, pisos y modo voz. Cambios aplicados en tiempo real.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setCfg(JSON.parse(JSON.stringify(initial)))}
            disabled={!dirty}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium disabled:opacity-40"
            style={{ border: '1px solid hsl(var(--border))', color: 'hsl(var(--text-muted))' }}
            data-testid="motor-reset"
          >
            <RotateCcw size={13} /> Descartar
          </button>
          <button
            type="button"
            onClick={save}
            disabled={!dirty || saving}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium disabled:opacity-40"
            style={{
              background: dirty ? 'hsl(var(--accent))' : 'hsl(var(--bg-elev))',
              color: dirty ? 'hsl(210 40% 8%)' : 'hsl(var(--text-muted))',
              fontWeight: 600,
            }}
            data-testid="motor-save"
          >
            <Save size={13} /> {saving ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </div>
      </div>

      {/* Flow diagram — visualizes the billing pipeline end-to-end
          without exposing raw numbers. Purely didactic. */}
      <div
        className="rounded-2xl p-5"
        style={{
          background: 'linear-gradient(150deg, hsl(var(--bg-elev) / 0.9), hsl(var(--bg-elev) / 0.5))',
          border: '1px solid hsl(var(--accent) / 0.25)',
          backdropFilter: 'blur(14px)',
        }}
        data-testid="motor-flow-diagram"
      >
        <div className="flex items-center gap-2 mb-4">
          <Sparkles size={16} style={{ color: 'hsl(var(--accent))' }} />
          <div className="text-sm font-medium">Cómo Nexus cobra cada acción</div>
          <div className="text-[10px] uppercase tracking-wider ml-auto opacity-60">Pipeline en vivo</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 items-stretch">
          {[
            { label: 'Input', hint: 'Tokens que entran al modelo', color: 'hsl(210 100% 65%)' },
            { label: 'Output', hint: 'Tokens que el modelo genera', color: 'hsl(210 100% 65%)' },
            { label: 'Costo real', hint: 'Precio real por 1M tokens', color: 'hsl(38 92% 60%)' },
            { label: 'Multiplicador', hint: `Global ${cfg.global_multiplier}× u override por intent`, color: 'hsl(var(--accent))' },
            { label: 'USD cobrado', hint: 'Ingreso bruto por la acción', color: 'hsl(var(--accent))' },
            { label: 'Piso mínimo', hint: 'Se compara max(cobro, piso)', color: 'hsl(38 92% 60%)' },
            { label: 'Créditos', hint: `1 crédito = $${cfg.credit_value_usd.toFixed(2)}`, color: 'hsl(155 65% 60%)' },
          ].map((step, i, arr) => (
            <React.Fragment key={step.label}>
              <div
                className="rounded-xl px-3 py-3 relative overflow-hidden"
                style={{
                  background: 'hsl(var(--bg) / 0.55)',
                  border: `1px solid ${step.color.replace(')', ' / 0.3)')}`,
                }}
              >
                <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: step.color }}>
                  {i + 1}. {step.label}
                </div>
                <div className="text-[11px] mt-1 leading-tight" style={{ color: 'hsl(var(--text-muted))' }}>
                  {step.hint}
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>
        <div className="mt-3 text-[11px] leading-relaxed" style={{ color: 'hsl(var(--text-muted))' }}>
          Fórmula: <code className="font-mono" style={{ color: 'hsl(var(--accent))' }}>credits = max(tokens × multiplicador, piso) / valor_credito</code>.
          Todo lo que cambias abajo se aplica inmediatamente en cada nueva acción del motor.
        </div>
      </div>

      {/* Row 1: global + credit value */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <GlassCard title="Multiplicador global" icon={<Sliders size={16} />}>
          <div className="flex items-center gap-3 mt-2">
            <input
              type="number" step="0.1" min={1} max={30}
              value={cfg.global_multiplier}
              onChange={(e) => setCfg({ ...cfg, global_multiplier: Number(e.target.value) })}
              className="w-24 rounded-md px-3 py-2 text-lg font-semibold"
              style={{ background: 'hsl(var(--bg))', border: '1px solid hsl(var(--border))', color: 'hsl(var(--accent))' }}
              data-testid="motor-global-multiplier"
            />
            <span className="text-xs" style={{ color: 'hsl(var(--text-muted))' }}>
              Default aplicado a cualquier intent sin override.
            </span>
          </div>
        </GlassCard>

        <GlassCard title="Valor por crédito (USD)" icon={<Coins size={16} />}>
          <div className="flex items-center gap-3 mt-2">
            <input
              type="number" step="0.01" min={0.01} max={5}
              value={cfg.credit_value_usd}
              onChange={(e) => setCfg({ ...cfg, credit_value_usd: Number(e.target.value) })}
              className="w-24 rounded-md px-3 py-2 text-lg font-semibold"
              style={{ background: 'hsl(var(--bg))', border: '1px solid hsl(var(--border))', color: 'hsl(var(--accent))' }}
              data-testid="motor-credit-value"
            />
            <span className="text-xs" style={{ color: 'hsl(var(--text-muted))' }}>
              1 crédito = ${cfg.credit_value_usd.toFixed(2)} USD
            </span>
          </div>
        </GlassCard>

        <GlassCard title="Créditos de bienvenida" icon={<Sparkles size={16} />}>
          <div className="flex items-center gap-3 mt-2">
            <input
              type="number" step="1" min={0} max={10000}
              value={cfg.welcome_gift_credits}
              onChange={(e) => setCfg({ ...cfg, welcome_gift_credits: Number(e.target.value) })}
              className="w-24 rounded-md px-3 py-2 text-lg font-semibold"
              style={{ background: 'hsl(var(--bg))', border: '1px solid hsl(var(--border))', color: 'hsl(var(--accent))' }}
              data-testid="motor-welcome"
            />
            <span className="text-xs" style={{ color: 'hsl(var(--text-muted))' }}>
              Gratis al registrarse.
            </span>
          </div>
        </GlassCard>
      </div>

      {/* Per-intent table */}
      <div
        className="rounded-2xl overflow-hidden"
        style={{
          background: 'linear-gradient(150deg, hsl(var(--bg-elev) / 0.9), hsl(var(--bg-elev) / 0.5))',
          border: '1px solid hsl(var(--border))',
          backdropFilter: 'blur(14px)',
        }}
      >
        <div
          className="flex items-center gap-2 px-5 py-4"
          style={{ borderBottom: '1px solid hsl(var(--border))' }}
        >
          <Sliders size={16} style={{ color: 'hsl(var(--accent))' }} />
          <div className="text-sm font-medium">Multiplicadores y pisos por tipo de trabajo</div>
          <div className="text-[10px] tracking-wider uppercase ml-auto flex items-center gap-1" style={{ color: 'hsl(var(--text-muted))' }}>
            <Info size={11} /> credits_charged = max(tokens × mult, floor)
          </div>
        </div>
        <div className="divide-y" style={{ borderColor: 'hsl(var(--border))' }}>
          {INTENT_ORDER.map((key) => {
            const meta = INTENT_META[key];
            const Icon = meta.icon;
            const mult = Number(cfg.intent_multipliers[key] ?? cfg.global_multiplier);
            const floors = cfg.min_charge_by_intent[key] || { default: 0 };
            const scopeKeys = Object.keys(floors);
            return (
              <div key={key} className="grid grid-cols-1 lg:grid-cols-12 gap-3 px-5 py-4 items-start" data-testid={`motor-row-${key}`}>
                <div className="lg:col-span-3 flex items-start gap-3">
                  <div
                    className="w-9 h-9 rounded-lg grid place-items-center shrink-0"
                    style={{
                      background: 'hsl(var(--accent) / 0.12)',
                      border: '1px solid hsl(var(--accent) / 0.3)',
                      color: 'hsl(var(--accent))',
                    }}
                  >
                    <Icon size={16} />
                  </div>
                  <div>
                    <div className="text-sm font-medium">{meta.label}</div>
                    <div className="text-[11px] leading-relaxed mt-0.5" style={{ color: 'hsl(var(--text-muted))' }}>
                      {meta.hint}
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-2 flex items-center gap-2">
                  <input
                    type="number" step="0.1" min={0.5} max={30}
                    value={mult}
                    onChange={(e) => setIntentMultiplier(key, Number(e.target.value))}
                    className="w-full rounded-md px-2 py-1.5 text-sm font-mono"
                    style={{ background: 'hsl(var(--bg))', border: '1px solid hsl(var(--border))', color: 'hsl(var(--accent))' }}
                    data-testid={`motor-mult-${key}`}
                    aria-label={`Multiplicador ${meta.label}`}
                  />
                  <span className="text-[10px] uppercase tracking-wider" style={{ color: 'hsl(var(--text-muted))' }}>×</span>
                </div>

                <div className="lg:col-span-7 flex flex-wrap gap-2">
                  {scopeKeys.map((s) => (
                    <div key={s} className="flex items-center gap-1.5" style={{ background: 'hsl(var(--bg) / 0.4)', border: '1px solid hsl(var(--border))', borderRadius: 6, padding: '4px 8px' }}>
                      <span className="text-[10px] uppercase tracking-wider" style={{ color: 'hsl(var(--text-muted))' }}>{s}</span>
                      <input
                        type="number" step="0.5" min={0} max={500}
                        value={floors[s]}
                        onChange={(e) => setFloor(key, s, Number(e.target.value))}
                        className="w-20 rounded-md px-2 py-1 text-xs font-mono"
                        style={{ background: 'hsl(var(--bg))', border: '1px solid hsl(var(--border))', color: 'hsl(var(--text))' }}
                        data-testid={`motor-floor-${key}-${s}`}
                        aria-label={`Piso ${meta.label} ${s}`}
                      />
                      <span className="text-[10px]" style={{ color: 'hsl(var(--text-muted))' }}>créd</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Voice mode config */}
      <div
        className="rounded-2xl p-5"
        style={{
          background: 'linear-gradient(150deg, hsl(var(--bg-elev) / 0.9), hsl(var(--bg-elev) / 0.5))',
          border: '1px solid hsl(var(--border))',
          backdropFilter: 'blur(14px)',
        }}
      >
        <div className="flex items-center gap-2 mb-4">
          <Mic size={16} style={{ color: 'hsl(var(--accent))' }} />
          <div className="text-sm font-medium">Modo voz</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-[10px] uppercase tracking-wider" style={{ color: 'hsl(var(--text-muted))' }}>Proveedor por defecto</label>
            <select
              value={cfg.voice_config.provider}
              onChange={(e) => setVoice({ provider: e.target.value })}
              className="mt-1 w-full rounded-md px-3 py-2 text-sm"
              style={{ background: 'hsl(var(--bg))', border: '1px solid hsl(var(--border))', color: 'hsl(var(--text))' }}
              data-testid="motor-voice-provider"
            >
              <option value="web_speech">Web Speech API (gratis)</option>
              <option value="openai_tts">OpenAI TTS</option>
              <option value="elevenlabs">ElevenLabs</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider" style={{ color: 'hsl(var(--text-muted))' }}>Precio por minuto (créd)</label>
            <input
              type="number" step="0.05" min={0} max={100}
              value={cfg.voice_config.price_per_minute_credits}
              onChange={(e) => setVoice({ price_per_minute_credits: Number(e.target.value) })}
              className="mt-1 w-full rounded-md px-3 py-2 text-sm font-mono"
              style={{ background: 'hsl(var(--bg))', border: '1px solid hsl(var(--border))', color: 'hsl(var(--accent))' }}
              data-testid="motor-voice-price"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider" style={{ color: 'hsl(var(--text-muted))' }}>Minutos gratis por día</label>
            <input
              type="number" step="1" min={0} max={9999}
              value={cfg.voice_config.free_minutes_per_day}
              onChange={(e) => setVoice({ free_minutes_per_day: Number(e.target.value) })}
              className="mt-1 w-full rounded-md px-3 py-2 text-sm font-mono"
              style={{ background: 'hsl(var(--bg))', border: '1px solid hsl(var(--border))', color: 'hsl(var(--text))' }}
              data-testid="motor-voice-free-mins"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-wider" style={{ color: 'hsl(var(--text-muted))' }}>Proveedor Delivery Card TTS</label>
            <select
              value={cfg.voice_config.premium_provider}
              onChange={(e) => setVoice({ premium_provider: e.target.value })}
              className="mt-1 w-full rounded-md px-3 py-2 text-sm"
              style={{ background: 'hsl(var(--bg))', border: '1px solid hsl(var(--border))', color: 'hsl(var(--text))' }}
              data-testid="motor-voice-premium-provider"
            >
              <option value="openai_tts">OpenAI TTS (calidad alta)</option>
              <option value="elevenlabs">ElevenLabs</option>
              <option value="web_speech">Web Speech API (gratis)</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider" style={{ color: 'hsl(var(--text-muted))' }}>Precio Delivery TTS (créd)</label>
            <input
              type="number" step="0.05" min={0} max={100}
              value={cfg.voice_config.premium_price_credits}
              onChange={(e) => setVoice({ premium_price_credits: Number(e.target.value) })}
              className="mt-1 w-full rounded-md px-3 py-2 text-sm font-mono"
              style={{ background: 'hsl(var(--bg))', border: '1px solid hsl(var(--border))', color: 'hsl(var(--accent))' }}
              data-testid="motor-voice-premium-price"
            />
          </div>
        </div>
      </div>

      {/* Simulator */}
      <div
        className="rounded-2xl p-5"
        style={{
          background: 'linear-gradient(150deg, hsl(var(--bg-elev) / 0.9), hsl(var(--bg-elev) / 0.5))',
          border: '1px solid hsl(var(--border))',
          backdropFilter: 'blur(14px)',
        }}
      >
        <div className="flex items-center gap-2 mb-3">
          <Volume2 size={16} style={{ color: 'hsl(var(--accent))' }} />
          <div className="text-sm font-medium">Simulación de consumo</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[
            { label: 'Landing profesional', tokens: 350_000, intent: 'create' as IntentKey, scope: 'landing' },
            { label: 'App funcional media', tokens: 900_000, intent: 'create' as IntentKey, scope: 'app_simple' },
            { label: 'App hospitalaria completa', tokens: 2_000_000, intent: 'create' as IntentKey, scope: 'app_full' },
          ].map((c) => {
            const mult = Number(cfg.intent_multipliers[c.intent] ?? cfg.global_multiplier);
            const usd = simCharged(c.tokens, mult);
            const creditsByTokens = simCredits(usd);
            const floor = (cfg.min_charge_by_intent[c.intent] || {})[c.scope] || 0;
            const finalCredits = Math.max(creditsByTokens, floor);
            const winner = creditsByTokens >= floor ? 'tokens' : 'piso';
            return (
              <div
                key={c.label}
                className="rounded-xl p-4"
                style={{ background: 'hsl(var(--bg) / 0.55)', border: '1px solid hsl(var(--border))' }}
                data-testid={`motor-sim-${c.scope}`}
              >
                <div className="text-[11px] uppercase tracking-wider" style={{ color: 'hsl(var(--text-muted))' }}>{c.label}</div>
                <div className="mt-2 flex items-baseline gap-2">
                  <div className="text-2xl font-bold" style={{ color: 'hsl(var(--accent))' }}>{finalCredits.toFixed(0)}</div>
                  <div className="text-[10px] uppercase tracking-wider" style={{ color: 'hsl(var(--text-muted))' }}>créditos</div>
                </div>
                <div className="mt-2 text-[10px] leading-relaxed" style={{ color: 'hsl(var(--text-muted))' }}>
                  {(c.tokens / 1000).toFixed(0)}K tokens × {mult}× → ${usd.toFixed(2)}<br />
                  tokens: {creditsByTokens.toFixed(1)} créd · piso: {floor} créd<br />
                  <span style={{ color: winner === 'tokens' ? 'hsl(155 65% 60%)' : 'hsl(38 92% 60%)' }}>
                    Gana: {winner}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const GlassCard: React.FC<React.PropsWithChildren<{ title: string; icon?: React.ReactNode }>> = ({ title, icon, children }) => (
  <div
    className="rounded-2xl p-4"
    style={{
      background: 'linear-gradient(150deg, hsl(var(--bg-elev) / 0.9), hsl(var(--bg-elev) / 0.5))',
      border: '1px solid hsl(var(--border))',
      backdropFilter: 'blur(14px)',
    }}
  >
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider" style={{ color: 'hsl(var(--text-muted))' }}>
      {icon}
      {title}
    </div>
    {children}
  </div>
);
